import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Archive, Download, Search, ShieldCheck, GitBranch, Rocket,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Clock, FileText,
  Package, BarChart3, AlertTriangle, ExternalLink, Lock, Database,
  Bug, Calendar, ShieldAlert,
} from "lucide-react";
import "./AdminUATCycles.css";
import "./AdminReleaseHistory.css";

const TARGET_META = {
  production: { label: "Production", color: "#065F46", bg: "#D1FAE5" },
  staging:    { label: "Staging",    color: "#1E40AF", bg: "#DBEAFE" },
  sandbox:    { label: "Sandbox",    color: "#7C3AED", bg: "#EDE9FE" },
  other:      { label: "Other",      color: "#4B5563", bg: "#E5E7EB" },
  unspecified:{ label: "Unspecified",color: "#4B5563", bg: "#E5E7EB" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function humanReadiness(v) {
  if (!v) return "—";
  const map = {
    pending_wave_c: "Pending",
    pending: "Pending",
    ready: "Ready",
    partial: "Partial",
    not_applicable: "N/A",
  };
  return map[v] || v.replace(/_/g, " ");
}

function humanOutcome(v) {
  if (!v) return "—";
  const map = {
    pending: "Awaiting deployment",
    success: "Success",
    failed: "Failed",
    rolled_back: "Rolled back",
  };
  return map[v] || v;
}

function TargetPill({ target }) {
  const key = target || "unspecified";
  const m = TARGET_META[key] || TARGET_META.other;
  return (
    <span className="hw-rh__pill" style={{ color: m.color, background: m.bg }}
          data-testid={`release-target-pill-${key}`}>
      <Rocket className="w-3 h-3" /> {m.label}
    </span>
  );
}

function KpiCard({ label, value, hint, icon: Icon, testId, tone = "default" }) {
  return (
    <div className={`hw-rh__kpi hw-rh__kpi--${tone}`} data-testid={testId}>
      <div className="hw-rh__kpi-label">
        {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
        {label}
      </div>
      <div className="hw-rh__kpi-value">{value}</div>
      {hint ? <div className="hw-rh__kpi-hint">{hint}</div> : null}
    </div>
  );
}

// ============================================================================
// Release Ledger — Administrator-only governance widget
// ----------------------------------------------------------------------------
// Displays the current production baseline at a glance. Sourced entirely
// from the immutable `release_history` collection (+ live open-critical
// defect count). INTERNAL ONLY — must not be surfaced on any public,
// partner, or funder view.
// ============================================================================
const READINESS_LABEL = {
  ready: "Ready",
  in_progress: "In Progress",
  partial: "Partial",
  pending: "Pending",
  pending_wave_c: "Pending",
  unknown: "Unknown",
  not_applicable: "N/A",
};
const READINESS_TONE = {
  ready: "pass",
  in_progress: "warn",
  partial: "warn",
  pending: "warn",
  pending_wave_c: "warn",
  unknown: "default",
  not_applicable: "default",
};
const OUTCOME_LABEL = {
  pending: "Awaiting deployment",
  success: "Success",
  failed: "Failed",
  rolled_back: "Rolled back",
};

function LedgerRow({ label, children, icon: Icon, testId }) {
  return (
    <div className="hw-rh-ledger__row" data-testid={testId}>
      <div className="hw-rh-ledger__row-label">
        {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
        {label}
      </div>
      <div className="hw-rh-ledger__row-value">{children}</div>
    </div>
  );
}

function ReleaseLedger({ data, loading, onRefresh }) {
  if (loading && !data) {
    return (
      <section className="hw-rh-ledger" data-testid="release-ledger">
        <div className="hw-rh-ledger__loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Release Ledger…
        </div>
      </section>
    );
  }
  if (!data || !data.current_release_id) {
    return (
      <section className="hw-rh-ledger" data-testid="release-ledger">
        <div className="hw-rh-ledger__header">
          <div className="hw-rh-ledger__title">
            <Lock className="w-4 h-4" /> Release Ledger
            <span className="hw-rh-ledger__admin-tag">Administrator only</span>
          </div>
        </div>
        <div className="hw-rh-ledger__empty">
          No production release has been archived yet.
        </div>
      </section>
    );
  }

  const complianceKey = data.compliance_readiness?.aggregate || "unknown";
  const complianceTone = READINESS_TONE[complianceKey] || "default";
  const deploymentOutcome = data.deployment_status?.outcome || "pending";
  const criticalTone = (data.open_critical_defects_count || 0) === 0 ? "pass" : "fail";
  const backup = data.last_successful_backup;
  const lastUat = data.last_successful_uat;
  const dayLabel = data.days_since_last_release == null
    ? "—"
    : (data.days_since_last_release < 1
        ? "Today"
        : `${data.days_since_last_release} day${data.days_since_last_release === 1 ? "" : "s"} ago`);

  return (
    <section className="hw-rh-ledger" data-testid="release-ledger">
      <div className="hw-rh-ledger__header">
        <div className="hw-rh-ledger__title">
          <Lock className="w-4 h-4" /> Release Ledger
          <span className="hw-rh-ledger__admin-tag" data-testid="ledger-admin-tag">
            Administrator only
          </span>
        </div>
        <button
          type="button"
          className="hw-rh__btn"
          onClick={onRefresh}
          data-testid="release-ledger-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="hw-rh-ledger__blurb">
        Governance snapshot of the current production baseline. Internal use
        only — not visible to clients, partner agencies, or funders.
      </p>

      <div className="hw-rh-ledger__grid">
        <LedgerRow label="Current production version" icon={GitBranch}
                   testId="ledger-version">
          <code className="hw-rh-ledger__code">{data.current_production_version}</code>
        </LedgerRow>

        <LedgerRow label="Current release ID" icon={Package}
                   testId="ledger-release-id">
          <code className="hw-rh-ledger__code">{data.current_release_id}</code>
          {data.current_release_name ? (
            <div className="hw-rh-ledger__sub">{data.current_release_name}</div>
          ) : null}
        </LedgerRow>

        <LedgerRow label="Release date" icon={Calendar}
                   testId="ledger-release-date">
          {fmtDate(data.release_date)}
        </LedgerRow>

        <LedgerRow label="Days since last release" icon={Clock}
                   testId="ledger-days-since">
          <strong>{dayLabel}</strong>
        </LedgerRow>

        <LedgerRow label="Deployment status" icon={Rocket}
                   testId="ledger-deployment-status">
          <span className={`hw-rh-ledger__pill hw-rh-ledger__pill--${
            deploymentOutcome === "success" ? "pass"
            : deploymentOutcome === "failed" || deploymentOutcome === "rolled_back" ? "fail"
            : "warn"
          }`}>
            {OUTCOME_LABEL[deploymentOutcome] || deploymentOutcome}
          </span>
          {data.deployment_status?.rollback_required ? (
            <span className="hw-rh-ledger__flag">Rollback required</span>
          ) : null}
        </LedgerRow>

        <LedgerRow label="Automation coverage" icon={BarChart3}
                   testId="ledger-automation">
          <span className={`hw-rh-ledger__pill hw-rh-ledger__pill--${
            (data.automation_coverage_pct || 0) >= 95 ? "pass"
            : (data.automation_coverage_pct || 0) >= 80 ? "warn" : "fail"
          }`}>
            {data.automation_coverage_pct != null ? `${data.automation_coverage_pct}%` : "—"}
          </span>
          {data.release_gate_score ? (
            <span className="hw-rh-ledger__flag">
              Gate {data.release_gate_score}
            </span>
          ) : null}
        </LedgerRow>

        <LedgerRow label="Compliance readiness" icon={ShieldCheck}
                   testId="ledger-compliance">
          <span className={`hw-rh-ledger__pill hw-rh-ledger__pill--${complianceTone}`}>
            {READINESS_LABEL[complianceKey] || complianceKey}
          </span>
          <div className="hw-rh-ledger__sub">
            {["hipaa", "soc2", "nist", "cfr42_part2"].map((k) => {
              const v = data.compliance_readiness?.frameworks?.[k] || "unknown";
              const label = k === "cfr42_part2" ? "42 CFR Part 2"
                          : k === "soc2" ? "SOC 2"
                          : k.toUpperCase();
              return (
                <span key={k} className="hw-rh-ledger__mini"
                      data-testid={`ledger-compliance-${k}`}>
                  <strong>{label}:</strong> {READINESS_LABEL[v] || v}
                </span>
              );
            })}
          </div>
        </LedgerRow>

        <LedgerRow label="Open critical defects" icon={Bug}
                   testId="ledger-open-critical">
          <span className={`hw-rh-ledger__pill hw-rh-ledger__pill--${criticalTone}`}>
            {data.open_critical_defects_count} <ShieldAlert className="w-3 h-3" />
          </span>
          <span className="hw-rh-ledger__flag">S1 + S2 across all cycles</span>
        </LedgerRow>

        <LedgerRow label="Last successful backup" icon={Database}
                   testId="ledger-last-backup">
          {backup ? (
            <>
              <div>{fmtDate(backup.verified_at)}</div>
              <div className="hw-rh-ledger__sub">
                <code className="hw-rh-ledger__code">
                  {backup.backup_location || "—"}
                </code>
              </div>
            </>
          ) : (
            <span className="hw-rh-ledger__pill hw-rh-ledger__pill--fail">
              No backup recorded
            </span>
          )}
        </LedgerRow>

        <LedgerRow label="Last successful UAT" icon={CheckCircle2}
                   testId="ledger-last-uat">
          {lastUat ? (
            <>
              <code className="hw-rh-ledger__code">{lastUat.release_id}</code>
              <div className="hw-rh-ledger__sub">
                Signed off {fmtDate(lastUat.signed_off_at)}
              </div>
            </>
          ) : "—"}
        </LedgerRow>
      </div>
    </section>
  );
}


// ============================================================================
// Detail Drawer
// ============================================================================
function ReleaseDetailDrawer({ releaseId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [release, setRelease] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/release-history/${releaseId}`);
      setRelease(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load release"));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [releaseId, onClose]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("hw_access_token");
      const resp = await fetch(
        `${api.defaults.baseURL}/admin/release-history/${releaseId}/download`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${releaseId}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Release package downloaded");
    } catch (e) {
      toast.error(formatApiError(e, "Download failed"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="hw-rh__drawer-backdrop" onClick={onClose}
         data-testid="release-detail-backdrop">
      <div
        className="hw-rh__drawer"
        role="dialog"
        aria-label="Release detail"
        onClick={(e) => e.stopPropagation()}
        data-testid="release-detail-drawer"
      >
        <div className="hw-rh__drawer-header">
          <div>
            <div className="hw-rh__drawer-crumb">Immutable Release Record</div>
            <div className="hw-rh__drawer-title">
              <Package className="w-4 h-4" />
              <code>{releaseId}</code>
            </div>
          </div>
          <div className="hw-rh__drawer-actions">
            <button
              type="button"
              className="hw-rh__btn hw-rh__btn--primary"
              onClick={download}
              disabled={downloading || loading}
              data-testid="release-download-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download ZIP
            </button>
            <button
              type="button"
              className="hw-uat-cycles__icon-btn"
              onClick={onClose}
              aria-label="Close"
              data-testid="release-detail-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="hw-rh__drawer-body">
          {loading || !release ? (
            <div className="hw-uat-cycles__loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading release…
            </div>
          ) : (
            <>
              <section className="hw-rh__section" data-testid="release-detail-governance">
                <h4>Governance</h4>
                <dl className="hw-rh__dl">
                  <dt>Release ID</dt><dd><code>{release.release_id}</code></dd>
                  <dt>Release name</dt><dd>{release.release_name}</dd>
                  <dt>Version</dt><dd>{release.version || "—"}</dd>
                  <dt>Git commit</dt><dd><code>{release.git_commit || "—"}</code></dd>
                  <dt>Git branch</dt><dd><code>{release.git_branch || "—"}</code></dd>
                  <dt>Build number</dt><dd>{release.build_number || "—"}</dd>
                  <dt>Environment</dt><dd><code>{release.environment}</code></dd>
                  <dt>Deployment target</dt><dd><TargetPill target={release.deployment_target} /></dd>
                  <dt>Signed off at</dt><dd>{fmtDate(release.signed_off_at)}</dd>
                  <dt>Approved by</dt><dd>{release.approved_by?.email || "—"}</dd>
                  <dt>Release manager</dt><dd>{release.release_manager?.email || "—"}</dd>
                  <dt>UAT lead</dt><dd>{release.uat_lead?.email || "—"}</dd>
                  <dt>Decision</dt><dd>{release.signoff_decision}</dd>
                  {release.signoff_conditions ? (
                    <><dt>Conditions</dt><dd>{release.signoff_conditions}</dd></>
                  ) : null}
                </dl>
              </section>

              <section className="hw-rh__section" data-testid="release-detail-quality">
                <h4>Quality</h4>
                <div className="hw-rh__grid-3">
                  <KpiCard label="Total tests" value={release.quality?.total_tests || 0} icon={FileText} testId="rh-quality-total" />
                  <KpiCard label="Passed" value={release.quality?.passed || 0} icon={CheckCircle2} testId="rh-quality-passed" tone="pass" />
                  <KpiCard label="Failed" value={release.quality?.failed || 0} icon={XCircle} testId="rh-quality-failed" tone="fail" />
                  <KpiCard label="Skipped" value={release.quality?.skipped || 0} icon={Clock} testId="rh-quality-skipped" />
                  <KpiCard label="Automation coverage" value={`${release.quality?.automation_coverage_pct || 0}%`} icon={BarChart3} testId="rh-quality-automation" />
                  <KpiCard label="Manual coverage" value={`${release.quality?.manual_coverage_pct || 0}%`} testId="rh-quality-manual" />
                  <KpiCard label="Waivers" value={release.quality?.waivers_count || 0} icon={AlertTriangle} testId="rh-quality-waivers" />
                  <KpiCard label="Release gate" value={release.quality?.release_gate_score || "—"} icon={ShieldCheck} testId="rh-quality-gate" tone={release.quality?.release_gate_ready ? "pass" : "fail"} />
                  <KpiCard label="Overall pass rate" value={`${release.quality?.overall_pass_rate_pct || 0}%`} testId="rh-quality-pass-rate" />
                </div>
                <div className="hw-rh__defect-line">
                  <strong>Defects by severity:</strong>{" "}
                  {["S1", "S2", "S3", "S4"].map((s) => (
                    <span key={s} className="hw-rh__pill" style={{
                      color: "#0F172A", background: "#F1F5F9", marginRight: "0.4rem",
                    }}>{s}: {release.quality?.defects_by_severity?.[s] ?? 0}</span>
                  ))}
                </div>
              </section>

              <section className="hw-rh__section" data-testid="release-detail-operational">
                <h4>Operational</h4>
                <dl className="hw-rh__dl">
                  <dt>Backup verification</dt>
                  <dd>{release.operational?.backup_verification?.verified ? "✅ Verified" : "❌ Not verified"} — {release.operational?.backup_verification?.total_verifications || 0} record(s)</dd>
                  <dt>Rollback validation</dt>
                  <dd>{release.operational?.rollback_validation?.validated ? `✅ Validated ${fmtDate(release.operational?.rollback_validation?.validated_at)}` : "❌ Not validated"}</dd>
                  <dt>Monitoring status</dt>
                  <dd>{release.operational?.monitoring_status ? "✅ Configured" : "❌ Not configured"}</dd>
                  <dt>Audit status</dt>
                  <dd>{release.operational?.audit_status ? "✅ Configured" : "❌ Not configured"}</dd>
                  <dt>Security status</dt>
                  <dd>{release.operational?.security_status || "—"}</dd>
                  <dt>Database schema version</dt>
                  <dd><code>{release.operational?.database_schema_version || "—"}</code></dd>
                  <dt>API version</dt>
                  <dd><code>{release.operational?.api_version || "—"}</code></dd>
                  <dt>Feature flags enabled</dt>
                  <dd>{(release.operational?.feature_flags_enabled || []).length === 0 ? "None" : release.operational.feature_flags_enabled.join(", ")}</dd>
                </dl>
              </section>

              <section className="hw-rh__section" data-testid="release-detail-compliance">
                <h4>Compliance</h4>
                <dl className="hw-rh__dl">
                  <dt>HIPAA readiness</dt><dd>{humanReadiness(release.compliance?.hipaa_readiness)}</dd>
                  <dt>SOC 2 readiness</dt><dd>{humanReadiness(release.compliance?.soc2_readiness)}</dd>
                  <dt>NIST readiness</dt><dd>{humanReadiness(release.compliance?.nist_readiness)}</dd>
                  <dt>42 CFR Part 2 readiness</dt><dd>{humanReadiness(release.compliance?.cfr42_part2_readiness)}</dd>
                  <dt>Compliance exceptions</dt><dd>{(release.compliance?.compliance_exceptions || []).length}</dd>
                </dl>
              </section>

              <section className="hw-rh__section" data-testid="release-detail-deployment">
                <h4>Deployment</h4>
                <dl className="hw-rh__dl">
                  <dt>Target</dt><dd><TargetPill target={release.deployment?.target} /></dd>
                  <dt>Duration</dt><dd>{release.deployment?.duration_seconds != null ? `${release.deployment.duration_seconds}s` : "Awaiting deployment"}</dd>
                  <dt>Outcome</dt><dd>{humanOutcome(release.deployment?.outcome)}</dd>
                  <dt>Rollback required</dt><dd>{release.deployment?.rollback_required ? "Yes" : "No"}</dd>
                  <dt>Post-deployment validation</dt><dd>{release.deployment?.post_deployment_validation || "Not run yet"}</dd>
                </dl>
              </section>

              <section className="hw-rh__section" data-testid="release-detail-traceability">
                <h4>Traceability</h4>
                <dl className="hw-rh__dl">
                  <dt>Linked UAT cycle</dt>
                  <dd><code>{release.traceability?.uat_cycle_id || release.uat_cycle_id}</code></dd>
                  <dt>Engineering standards</dt>
                  <dd>v{release.traceability?.engineering_standards_version}</dd>
                  <dt>Release artifacts</dt>
                  <dd>
                    <ul className="hw-rh__artifact-list">
                      {(release.traceability?.release_artifacts || []).map((a) => (
                        <li key={a}><code>{a}</code></li>
                      ))}
                    </ul>
                  </dd>
                </dl>
              </section>

              {(release.waivers || []).length > 0 && (
                <section className="hw-rh__section" data-testid="release-detail-waivers">
                  <h4>Waivers ({release.waivers.length})</h4>
                  <ul className="hw-rh__waivers">
                    {release.waivers.map((w) => (
                      <li key={w.test_id}>
                        <strong><code>{w.test_id}</code></strong> — {w.title} <em>({w.severity})</em>
                        <div className="hw-rh__waiver-meta">
                          Deferred to <strong>{w.waiver?.deferred_to}</strong> · Approved by {w.waiver?.approved_by}
                        </div>
                        <div className="hw-rh__waiver-reason">{w.waiver?.reason}</div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {release.zip_sha256 ? (
                <section className="hw-rh__section" data-testid="release-detail-integrity">
                  <h4>Digital Integrity</h4>
                  <dl className="hw-rh__dl">
                    <dt>Algorithm</dt><dd>SHA-256</dd>
                    <dt>Release archive SHA-256</dt>
                    <dd><code style={{ wordBreak: "break-all" }}>{release.zip_sha256}</code></dd>
                    <dt>Archive size</dt>
                    <dd>{release.zip_size_bytes != null ? `${(release.zip_size_bytes / 1024).toFixed(1)} KB` : "—"}</dd>
                    <dt>Governance snapshot</dt>
                    <dd>schema v{release.governance_snapshot?.schema_version || "1.0.0"} — bundled as <code>Governance_Snapshot.json</code></dd>
                    <dt>Verify integrity</dt>
                    <dd>
                      <button
                        type="button"
                        className="hw-rh__btn"
                        onClick={async () => {
                          try {
                            const { data } = await api.get(`/admin/release-history/${releaseId}/verify`);
                            if (data.verified && data.zip_sha256_matches_archive) {
                              toast.success(`Integrity OK — ${data.verified_files}/${data.expected_files} files verified. Manifest hash matches.`);
                            } else {
                              toast.error(`Integrity mismatch — see verify report`);
                            }
                          } catch (e) {
                            toast.error(formatApiError(e, "Integrity verification failed"));
                          }
                        }}
                        data-testid="release-verify-integrity-btn"
                      >
                        <ShieldCheck className="w-4 h-4" /> Verify SHA-256
                      </button>
                    </dd>
                  </dl>
                </section>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Dashboard
// ============================================================================
function ReleaseDashboard({ data }) {
  if (!data) return null;
  return (
    <section className="hw-rh__dashboard" data-testid="release-dashboard">
      <h3 className="hw-rh__dashboard-heading">
        <BarChart3 className="w-4 h-4" /> Release Dashboard
      </h3>
      <div className="hw-rh__grid-4">
        <KpiCard label="Total releases" value={data.total_releases}
                 hint={`${data.total_production_releases || 0} to production`}
                 icon={Archive} testId="dash-total-releases" />
        <KpiCard label="Current production version"
                 value={data.current_production_version || "—"}
                 icon={GitBranch} testId="dash-current-prod-version" />
        <KpiCard label="Last successful release"
                 value={data.last_successful_release?.release_id || "—"}
                 hint={fmtDate(data.last_successful_release?.signed_off_at)}
                 icon={CheckCircle2} testId="dash-last-release" tone="pass" />
        <KpiCard label="Previous release"
                 value={data.previous_release?.release_id || "—"}
                 hint={fmtDate(data.previous_release?.signed_off_at)}
                 testId="dash-previous-release" />
        <KpiCard label="Release success rate"
                 value={`${data.release_success_rate_pct}%`}
                 icon={ShieldCheck}
                 tone={data.release_success_rate_pct >= 95 ? "pass" : "warn"}
                 testId="dash-success-rate" />
        <KpiCard label="Average automation coverage"
                 value={`${data.average_automation_coverage_pct}%`}
                 icon={BarChart3} testId="dash-avg-automation" />
        <KpiCard label="Average deployment time"
                 value={data.average_deployment_time_seconds != null
                        ? `${data.average_deployment_time_seconds}s`
                        : "—"}
                 icon={Clock} testId="dash-avg-deploy-time" />
        <KpiCard label="Mean time between releases"
                 value={data.mean_time_between_releases_hours != null
                        ? `${data.mean_time_between_releases_hours}h`
                        : "—"}
                 icon={Clock} testId="dash-mtbr" />
        <KpiCard label="Rollbacks"
                 value={data.rollbacks_count}
                 icon={RefreshCw}
                 tone={data.rollbacks_count === 0 ? "pass" : "warn"}
                 testId="dash-rollbacks" />
        <KpiCard label="Production hotfixes"
                 value={data.production_hotfixes_count}
                 icon={AlertTriangle}
                 testId="dash-hotfixes" />
        <KpiCard label="Outstanding waivers"
                 value={data.outstanding_waivers}
                 icon={AlertTriangle}
                 tone={data.outstanding_waivers === 0 ? "pass" : "warn"}
                 testId="dash-outstanding-waivers" />
      </div>
    </section>
  );
}


// ============================================================================
// Main page
// ============================================================================
export default function AdminReleaseHistory() {
  const [loading, setLoading] = useState(true);
  const [releases, setReleases] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [q, setQ] = useState("");
  const [targetFilter, setTargetFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [listResp, dashResp] = await Promise.all([
        api.get("/admin/release-history", { params: {
          q: q || undefined,
          target: targetFilter || undefined,
        }}),
        api.get("/admin/release-history/dashboard"),
      ]);
      setReleases(listResp.data.releases || []);
      setDashboard(dashResp.data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load release history"));
    } finally {
      setLoading(false);
    }
  }, [q, targetFilter]);

  const loadLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const { data } = await api.get("/admin/release-history/ledger");
      setLedger(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load release ledger"));
    } finally {
      setLedgerLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadLedger(); }, [loadLedger]);

  const filtered = useMemo(() => releases, [releases]);

  return (
    <div className="hw-uat-cycles" data-testid="admin-release-history">
      <div className="hw-uat-cycles__header">
        <div>
          <h2>
            <Archive className="w-5 h-5" style={{ display: "inline", marginRight: "0.4rem", verticalAlign: "text-bottom" }} />
            Immutable Release History
          </h2>
          <p className="hw-uat-cycles__subtitle">
            Permanent, tamper-resistant archive of every signed-off UAT cycle.
            Each release ships with a full audit package (ZIP) containing the
            release notes, gate checklist, rollback runbook, backup verification,
            defect summary, waivers, and cryptographic checksums.
          </p>
        </div>
        <button
          type="button"
          className="hw-rh__btn"
          onClick={() => { loadAll(); loadLedger(); }}
          data-testid="release-history-refresh"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <ReleaseLedger data={ledger} loading={ledgerLoading}
                     onRefresh={loadLedger} />

      <ReleaseDashboard data={dashboard} />

      <div className="hw-rh__filters" data-testid="release-history-filters">
        <div className="hw-rh__search">
          <Search className="w-4 h-4" />
          <input
            type="text"
            placeholder="Search by release ID, name, version, or commit…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="release-history-search"
          />
        </div>
        <select
          value={targetFilter}
          onChange={(e) => setTargetFilter(e.target.value)}
          className="hw-rh__select"
          data-testid="release-history-target-filter"
        >
          <option value="">All targets</option>
          <option value="production">Production</option>
          <option value="staging">Staging</option>
          <option value="sandbox">Sandbox</option>
          <option value="other">Other</option>
        </select>
      </div>

      {loading ? (
        <div className="hw-uat-cycles__loading" data-testid="release-history-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading releases…
        </div>
      ) : filtered.length === 0 ? (
        <div className="hw-uat-cycles__empty" data-testid="release-history-empty">
          <Archive className="w-6 h-6" />
          <div><strong>No releases archived yet.</strong></div>
          <div style={{ fontSize: "0.85rem", color: "#64748B" }}>
            Sign off a UAT cycle to create the first immutable release record.
          </div>
        </div>
      ) : (
        <div className="hw-rh__list" data-testid="release-history-list">
          {filtered.map((r) => (
            <button
              key={r.release_id}
              type="button"
              className="hw-rh__row"
              onClick={() => setSelectedId(r.release_id)}
              data-testid={`release-row-${r.release_id}`}
            >
              <div className="hw-rh__row-main">
                <div className="hw-rh__row-title">
                  <Package className="w-4 h-4" />
                  <code>{r.release_id}</code>
                  <span className="hw-rh__row-name">{r.release_name}</span>
                </div>
                <div className="hw-rh__row-meta">
                  <TargetPill target={r.deployment_target} />
                  <span className="hw-rh__row-metaitem">
                    <GitBranch className="w-3 h-3" /> <code>{r.git_commit || "—"}</code>
                  </span>
                  <span className="hw-rh__row-metaitem">v{r.version || "—"}</span>
                  <span className="hw-rh__row-metaitem">
                    <ShieldCheck className="w-3 h-3" /> {r.quality?.release_gate_score || "—"}
                  </span>
                  <span className="hw-rh__row-metaitem">
                    <BarChart3 className="w-3 h-3" /> {r.quality?.automation_coverage_pct || 0}% auto
                  </span>
                  <span className="hw-rh__row-metaitem">
                    <Clock className="w-3 h-3" /> {fmtDate(r.signed_off_at)}
                  </span>
                </div>
              </div>
              <div className="hw-rh__row-cta">
                <ExternalLink className="w-4 h-4" />
                <ChevronRight className="w-4 h-4" />
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedId ? (
        <ReleaseDetailDrawer
          releaseId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      ) : null}
    </div>
  );
}
